import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { transitionRuleService } from '@services';

export const useTransitionRules = (projectId, options = {}) => {
  return useQuery({
    queryKey: ['transitionRules', projectId],
    queryFn: () => transitionRuleService.getAll(projectId),
    enabled: !!projectId,
    staleTime: 30000, // Cache for 30 seconds
    ...options,
  });
};

export const useCreateTransitionRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: transitionRuleService.create,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['transitionRules', variables.projectId] });
    },
  });
};

export const useDeleteTransitionRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: transitionRuleService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transitionRules'] });
    },
  });
};

export const useBulkUpdateTransitionRules = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, rules }) => transitionRuleService.bulkUpdate(projectId, rules),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['transitionRules', projectId] });
    },
  });
};

// Helper hook to check allowed transitions
export const useAllowedTransitions = (projectId) => {
  const { data, isLoading } = useTransitionRules(projectId);

  const rules = data?.data || [];
  const hasRules = rules.length > 0;

  // Create a map for fast lookups: { "fromStatusName": ["toStatusName1", "toStatusName2"] }
  const transitionMap = useMemo(() => {
    if (!hasRules) return null;

    const map = {};
    rules.forEach((rule) => {
      const fromName = rule.fromStatus.name;
      if (!map[fromName]) {
        map[fromName] = new Set();
      }
      map[fromName].add(rule.toStatus.name);
    });

    // Convert Sets to arrays
    Object.keys(map).forEach((key) => {
      map[key] = Array.from(map[key]);
    });

    return map;
  }, [rules, hasRules]);

  const isTransitionAllowed = (fromStatusName, toStatusName) => {
    // If no rules exist, all transitions are allowed
    if (!hasRules) return true;

    // Same status is always allowed
    if (fromStatusName === toStatusName) return true;

    // Check if the transition exists in our map
    return transitionMap?.[fromStatusName]?.includes(toStatusName) ?? false;
  };

  const getAllowedTargets = (fromStatusName) => {
    // If no rules exist, return null (meaning all are allowed)
    if (!hasRules) return null;

    return transitionMap?.[fromStatusName] || [];
  };

  return {
    isTransitionAllowed,
    getAllowedTargets,
    hasRules,
    isLoading,
    rules,
  };
};
